import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { ReportingScope } from '@/domain/reportingCompare';
import { singleStudioScope } from '@/domain/reportingCompare';
import { Money } from '@/components/reporting/Money';
import InfoTip from '@/components/InfoTip';
import { ReportBarChart, ReportLineChart } from '@/components/reporting/ReportChart';
import { REPORT_COLORS } from '@/components/reporting/reportChartTheme';
import { revenueByStore, dayKeyOf, daysInRange, reportingNow } from '@/domain/reportingEngine';
import { networkRevenueByWeek } from '@/domain/ownerRevenue';

function weekLabel(weekEndIso: string): string {
  return new Date(`${weekEndIso}T12:00:00+10:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/**
 * Owner tab (leak-first reporting, Task 12). A revenue chart, deliberately not
 * a P&L: network revenue this period, month-to-date, and studio take up top;
 * revenue-by-shop split ours-vs-artist; and the network's weekly revenue
 * trend. Owner-only - the shell mounts this tab only when `moneyVisible`, but
 * every dollar still renders through `<Money>` so the shell's gate stays the
 * only one.
 */
export default function OwnerTab({ scope }: { scope: ReportingScope }) {
  const studios = useBrainStore((s) => s.studios);
  const bookings = useBrainStore((s) => s.bookings);
  const salePayments = useBrainStore((s) => s.salePayments);
  const artists = useBrainStore((s) => s.artists);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const { days, moneyVisible, studioIds } = scope;

  const single = singleStudioScope(studioIds);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const perStore = useMemo(
    () => revenueByStore(days, single),
    [days, single, bookings, salePayments, artists, studios],
  );

  const mtdDays = useMemo(() => {
    const now = reportingNow();
    const todayKey = dayKeyOf(now);
    const firstOfMonthKey = `${todayKey.slice(0, 8)}01`;
    return daysInRange(firstOfMonthKey, todayKey);
  }, [clockIso]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mtdPerStore = useMemo(
    () => revenueByStore(mtdDays, single),
    [mtdDays, single, bookings, salePayments, artists, studios],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const weekly = useMemo(
    () => networkRevenueByWeek(reportingNow(), 8),
    [clockIso, bookings, salePayments, artists],
  );

  const networkTotalAud = perStore.reduce((a, r) => a + r.totalAud, 0);
  const mtdTotalAud = mtdPerStore.reduce((a, r) => a + r.totalAud, 0);
  const studioTakeAud = perStore.reduce((a, r) => a + r.oursAud, 0);

  const byShopRows = perStore.map((r) => ({ shop: r.name, Ours: r.oursAud, Artists: r.artistAud }));
  const weeklyRows = weekly.map((w) => ({ week: weekLabel(w.weekEndIso), revenue: w.totalAud }));

  return (
    <div className="space-y-6 max-w-6xl" data-testid="reporting-owner">
      <section className="grid grid-cols-3 gap-3">
        <Tile testId="owner-tile-network-revenue" label="Network revenue · this period">
          <Money amountAud={networkTotalAud} visible={moneyVisible} />
        </Tile>
        <Tile testId="owner-tile-mtd" label="Month to date">
          <Money amountAud={mtdTotalAud} visible={moneyVisible} />
        </Tile>
        <Tile testId="owner-tile-studio-take" label="Studio take · this period">
          <Money amountAud={studioTakeAud} visible={moneyVisible} />
        </Tile>
      </section>

      <section className="panel p-4" data-testid="owner-by-shop">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Revenue by shop
          <InfoTip>Completed-session revenue over the range, split house vs artist, per studio.</InfoTip>
        </h2>
        <ReportBarChart
          data={byShopRows}
          xKey="shop"
          stacked
          series={[
            { key: 'Ours', name: 'Ours (house)', color: REPORT_COLORS.gold },
            { key: 'Artists', name: 'Artists', color: REPORT_COLORS.azure },
          ]}
        />
      </section>

      <section className="panel p-4" data-testid="owner-weekly-trend">
        <h2 className="heading-serif text-lg text-cream mb-1">
          Weekly revenue trend
          <InfoTip>Network revenue totalled per week, last 8 weeks - every studio, not just the selection.</InfoTip>
        </h2>
        <ReportLineChart
          data={weeklyRows}
          xKey="week"
          hideLegend
          series={[{ key: 'revenue', name: 'Network revenue', color: REPORT_COLORS.gold }]}
        />
      </section>
    </div>
  );
}

function Tile({ label, testId, children }: { label: string; testId?: string; children: ReactNode }) {
  return (
    <div className="panel-dark p-4" data-testid={testId}>
      <p className="label-small-caps">{label}</p>
      <p className="font-cinzel text-3xl text-cream mt-2">{children}</p>
    </div>
  );
}
